/**
 * V2SettingsPage — dark, dense V2 restyle of the classic Profile/Settings
 * screen (`web/src/pages/ProfilePage.tsx`).
 *
 * Behavior parity with classic: same load/save logic, same backend methods.
 *   - Load:   BackendAPI.getUserPreferences()                  (GET /intelligent-scope/profiles/me)
 *   - Save scope structure: BackendAPI.updateScopeConfig()     (PUT .../scope-config)
 *   - Save generation defaults: BackendAPI.updateGenerationPrefs() (PUT .../generation)
 * Resilient fallback to client defaults on load failure (mirrors classic).
 *
 * Net-new vs classic: an "Appearance" section wired to the persisted
 * `useUiStore` (theme / accent / density) — a real V2 feature. No invented
 * backend settings.
 *
 * Layout (founder 2026-10-28): one tab per section, linkable as `?tab=<id>`, with a sticky
 * tab bar and a wide (1280px) page. Every panel stays mounted and inactive ones are
 * `hidden`, so an unsaved edit (here or inside HeadingsSection) survives a tab switch.
 */

import { useEffect, useState } from 'react';
import useAuthStore from '../../stores/authStore';
import { BackendAPI } from '../../lib/api';
import type {
  UserPreferences,
  ScopeConfig,
  ScopeHeading,
  GenerationPreferences,
} from '../../types';

import { Button } from '../components/ui/button';
import { Pill } from '../components/ui/pill';
import { Avatar } from '../components/ui/avatar';
import { Banner } from '../components/ui/banner';
import { Skeleton } from '../components/ui/skeleton';
import PlanCreditsSection from '../components/PlanCreditsSection';
import HeadingsSection from '../components/HeadingsSection'; // PRP-MCP-15 (was CompanyProfileSection, PRP-MCP-14 B)
import ReferralsSection from '../components/ReferralsSection'; // PRP-MCP-10: affiliates only
import AffiliateInvite from '../components/AffiliateInvite';
import { Section, type SaveFlash } from '../components/settings/SettingsFrame';
import {
  SettingsTabBar,
  useSettingsTab,
  type SettingsTab,
} from '../components/settings/SettingsTabBar';
import AppearancePanel from '../components/settings/AppearancePanel';
import PriceNotesPanel from '../components/settings/PriceNotesPanel';
import ScopeStructurePanel from '../components/settings/ScopeStructurePanel';
import GenerationDefaultsPanel from '../components/settings/GenerationDefaultsPanel';
import { IconWarn, IconLogout } from '../components/icons';

// ---------------------------------------------------------------------------
// Parity constants — copied verbatim from classic ProfilePage so generated
// defaults and the resilient fallback behave identically.
// ---------------------------------------------------------------------------

const CLIENT_DEFAULT_PREFS: UserPreferences = {
  scope_config: {
    structure_type: 'top_down',
    headings: [],
    damage_type_mapping: { flood: 'bottom_up', water: 'bottom_up' },
  },
  generation: {
    default_damage_type: null,
    default_jurisdiction: null,
    helpful_mode: false,
    cat3_default: false,
  },
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/** V2 Settings page (default export). */
export default function V2SettingsPage() {
  const { user, roles, logout } = useAuthStore();

  // Referrals is offered only once /me/referrals lists an affiliate code.
  const [hasReferrals, setHasReferrals] = useState(false);
  const offered = (id: SettingsTab) => id !== 'referrals' || hasReferrals;
  const [tab, setTab] = useSettingsTab(offered);

  // Preferences load/save — identical surface to classic ProfilePage.
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingScope, setSavingScope] = useState(false);
  const [savingGen, setSavingGen] = useState(false);
  const [loadWarning, setLoadWarning] = useState<string | null>(null);
  const [scopeFlash, setScopeFlash] = useState<SaveFlash>(null);
  const [genFlash, setGenFlash] = useState<SaveFlash>(null);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const prefs = await BackendAPI.getUserPreferences();
        setPreferences({
          ...prefs,
          generation: prefs.generation ?? CLIENT_DEFAULT_PREFS.generation,
        });
      } catch {
        // Resilient fallback: render defaults rather than a dead error page.
        setLoadWarning(
          'Could not load saved settings — showing defaults. Saving will create them.',
        );
        setPreferences(CLIENT_DEFAULT_PREFS);
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  }, []);

  const updateScope = (patch: Partial<ScopeConfig>) => {
    setPreferences((prev) =>
      prev ? { ...prev, scope_config: { ...prev.scope_config, ...patch } } : prev,
    );
  };

  const updateGeneration = (patch: Partial<GenerationPreferences>) => {
    setPreferences((prev) =>
      prev
        ? {
            ...prev,
            generation: { ...(prev.generation ?? CLIENT_DEFAULT_PREFS.generation!), ...patch },
          }
        : prev,
    );
  };

  const setHeadings = (headings: ScopeHeading[]) => {
    // Re-number order to match array position so the backend persists the
    // intended sequence (parity with classic).
    updateScope({ headings: headings.map((h, i) => ({ ...h, order: i })) });
  };

  const moveHeading = (index: number, dir: -1 | 1) => {
    if (!preferences) return;
    const list = [...preferences.scope_config.headings];
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setHeadings(list);
  };

  const handleSaveScope = async () => {
    if (!preferences) return;
    setSavingScope(true);
    setScopeFlash(null);
    try {
      const updated = await BackendAPI.updateScopeConfig(preferences.scope_config);
      setPreferences((prev) => (prev ? { ...prev, scope_config: updated.scope_config } : prev));
      setLoadWarning(null);
      setScopeFlash({ kind: 'ok', msg: 'Scope structure saved.' });
    } catch (err) {
      setScopeFlash({
        kind: 'bad',
        msg: `Error saving scope settings: ${err instanceof Error ? err.message : 'unknown error'}`,
      });
    } finally {
      setSavingScope(false);
    }
  };

  const handleSaveGeneration = async () => {
    if (!preferences?.generation) return;
    setSavingGen(true);
    setGenFlash(null);
    try {
      const updated = await BackendAPI.updateGenerationPrefs(preferences.generation);
      setPreferences((prev) => (prev ? { ...prev, generation: updated.generation } : prev));
      setLoadWarning(null);
      setGenFlash({ kind: 'ok', msg: 'Generation defaults saved.' });
    } catch (err) {
      setGenFlash({
        kind: 'bad',
        msg: `Error saving generation settings: ${err instanceof Error ? err.message : 'unknown error'}`,
      });
    } finally {
      setSavingGen(false);
    }
  };

  const displayName = user?.displayName || 'User';
  const prefs = preferences ?? CLIENT_DEFAULT_PREFS;
  const generation = prefs.generation ?? CLIENT_DEFAULT_PREFS.generation!;

  /** A tab's panel: always mounted (state survives a switch), hidden unless active. */
  const panel = (id: SettingsTab, children: React.ReactNode) => (
    <div role="tabpanel" hidden={tab !== id} data-testid={`settings-panel-${id}`}>
      {children}
    </div>
  );

  return (
    <div className="w-full" data-testid="settings-page">
      {/* page head */}
      <div className="mx-auto max-w-[1280px] px-6 pt-5 pb-3">
        <h1 className="m-0 text-[20px] font-[560] tracking-[-0.01em] text-[var(--tx-hi)]">
          Settings
        </h1>
        <p className="mt-1 mb-0 text-[13px] text-[var(--tx-lo)]">
          Manage your account and how scopes are generated.
        </p>
      </div>

      <SettingsTabBar tab={tab} onChange={setTab} offered={offered} />

      <div className="mx-auto max-w-[1280px] px-6 py-5">
        {loadWarning && (
          <Banner tone="warn" icon={<IconWarn />} className="mb-5">
            {loadWarning}
          </Banner>
        )}

        {panel(
          'profile',
          <>
            <Section title="Profile" sub="Linked to your authentication provider.">
              {loading ? (
                <div className="flex items-center gap-[14px]">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-56" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-[14px]">
                  <Avatar name={displayName} size={48} />
                  <div className="min-w-0">
                    <div className="text-[15px] font-[600] text-[var(--tx-hi)] truncate">
                      {displayName}
                    </div>
                    <div className="text-[12.5px] text-[var(--tx-lo)] truncate">
                      {user?.email ?? 'No email on file'}
                    </div>
                    {roles.length > 0 && (
                      <div className="mt-[6px] flex flex-wrap gap-[6px]">
                        {roles.map((r) => (
                          <Pill key={r} kind="neutral">
                            {r}
                          </Pill>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Section>
            {/* The in-app way to the affiliate application; an affiliate already has the Referrals tab. */}
            {!hasReferrals && (
              <div className="mt-5">
                <Section title="Affiliate program">
                  <AffiliateInvite />
                </Section>
              </div>
            )}
          </>,
        )}

        {panel('headings', <HeadingsSection frame={Section} />)}

        {panel('price-notes', <PriceNotesPanel />)}

        {panel(
          'scope',
          <ScopeStructurePanel
            config={prefs.scope_config}
            loading={loading}
            saving={savingScope}
            flash={scopeFlash}
            onSave={handleSaveScope}
            updateScope={updateScope}
            setHeadings={setHeadings}
            moveHeading={moveHeading}
          />,
        )}

        {panel(
          'generation',
          <GenerationDefaultsPanel
            generation={generation}
            loading={loading}
            saving={savingGen}
            flash={genFlash}
            onSave={handleSaveGeneration}
            updateGeneration={updateGeneration}
          />,
        )}

        {panel('appearance', <AppearancePanel />)}

        {/* PRP-MCP-9: plan, included conversions this month, pack credits. */}
        {/* Label/value rows: capped so the value stays near its label on a wide screen. */}
        {panel('plan', <div className="max-w-[760px]"><PlanCreditsSection frame={Section} /></div>)}

        {panel('referrals', <ReferralsSection onAvailable={setHasReferrals} />)}

        {panel(
          'session',
          <Section title="Session">
            <div className="flex items-center justify-between gap-4">
              <div className="text-[12.5px] text-[var(--tx-lo)]">
                Sign out of this device. You can sign back in any time.
              </div>
              <Button variant="danger" onClick={() => logout()}>
                <IconLogout />
                Sign out
              </Button>
            </div>
          </Section>,
        )}
      </div>
    </div>
  );
}
